import { z } from 'zod'

/**
 * The closed set of error codes (plan §5.2). Closed is the point: a client can
 * exhaustively switch on this union and the compiler will tell it when a new
 * failure mode appears. Adding a code here is a deliberate contract change.
 */
export const ERROR_CODES = [
  'VALIDATION_FAILED',
  'UNAUTHENTICATED',
  'INVALID_CREDENTIALS',
  'ACCOUNT_LOCKED',
  'FORBIDDEN',
  'PRICE_LOCKED',
  'SUBSCRIPTION_INACTIVE',
  'TENANT_SUSPENDED',
  'MAINTENANCE_MODE',
  'NOT_FOUND',
  'CONFLICT',
  'PLAN_LIMIT_EXCEEDED',
  'PAYLOAD_TOO_LARGE',
  'UNSUPPORTED_MEDIA_TYPE',
  'RATE_LIMITED',
  'INTERNAL',
] as const

export type ErrorCode = (typeof ERROR_CODES)[number]

export interface ErrorDefinition {
  /** The HTTP status this code is always returned with. One code, one status. */
  readonly status: number
  /**
   * Shown when the endpoint has nothing more specific to say. Safe to display:
   * it never names a table, a column, or another tenant's data.
   */
  readonly message: string
}

/**
 * Typed as `Record<ErrorCode, …>` so the compiler rejects both a missing entry
 * and one that is not in the union — the catalogue cannot drift from the set.
 */
export const ERROR_CATALOG: Record<ErrorCode, ErrorDefinition> = {
  VALIDATION_FAILED: { status: 400, message: 'Some of the details are not valid.' },
  UNAUTHENTICATED: { status: 401, message: 'Please sign in again.' },
  // Never says which half was wrong; that would enumerate usernames.
  INVALID_CREDENTIALS: { status: 401, message: 'Username or password is incorrect.' },
  ACCOUNT_LOCKED: { status: 423, message: 'Too many attempts. Try again shortly.' },
  FORBIDDEN: { status: 403, message: 'You do not have permission to do that.' },
  /**
   * PRICE_LOCKED is not "the figure is hidden" — under price lock the money key
   * is *absent from the payload* (§4.16), so nothing to unmask sits in the
   * response. This code is what a money-only endpoint returns instead of a body.
   */
  PRICE_LOCKED: { status: 403, message: 'Prices are hidden. Unlock to continue.' },
  SUBSCRIPTION_INACTIVE: { status: 403, message: 'This book is read-only until the plan is renewed.' },
  /**
   * C10, decided 10 September 2026: suspension is read-only, not lockout. So
   * this is never the answer to a sign-in or to a `GET` — it is only ever what
   * a *write* gets back, and the message has to say so or a trader staring at
   * his own ledger will think the screen is lying to him.
   */
  TENANT_SUSPENDED: {
    status: 403,
    message: 'This book is suspended. You can still read it; nothing can be changed.',
  },
  MAINTENANCE_MODE: { status: 403, message: 'Cattle Book is briefly down for maintenance.' },
  /**
   * NOT_FOUND is also the answer for a row that exists but belongs to another
   * tenant. A 403 there would confirm the row exists, which is a tenant-
   * enumeration leak; "missing" and "not yours" must be indistinguishable.
   */
  NOT_FOUND: { status: 404, message: 'Not found.' },
  CONFLICT: { status: 409, message: 'That conflicts with something already recorded.' },
  PLAN_LIMIT_EXCEEDED: { status: 409, message: 'This plan has reached its limit.' },
  PAYLOAD_TOO_LARGE: { status: 413, message: 'That file is too large.' },
  UNSUPPORTED_MEDIA_TYPE: { status: 415, message: 'That file is not a photo or video.' },
  RATE_LIMITED: { status: 429, message: 'Too many requests. Please wait a moment.' },
  INTERNAL: { status: 500, message: 'Something went wrong. Please try again.' },
}

export const errorCodeSchema = z.enum(ERROR_CODES)

export const httpStatusFor = (code: ErrorCode): number => ERROR_CATALOG[code].status

export const defaultMessageFor = (code: ErrorCode): string => ERROR_CATALOG[code].message
